import { useEffect, useState } from "react";
import {
  ImageUpload,
  deleteLocalAndGlobalAssetImages,
  getLocalAndGlobalAssetImages,
} from "../model/ImageUpload";
import Logger from "../../../../basecomp/base/Logger";
import { useToast } from "../../../../basecomp/components/provider/ToastProvider";
import {
  AssetNumberRequired,
  MaxImageSelect,
  ImageRequired,
  maxSizePerFile,
  imageSize,
  MaxImageUpload,
  AssetImageDeleted,
} from "../../assets/Strings";

const ImageUploadVM = () => {
  const [assetId, setAssetId] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const { handleToastOpen } = useToast();

  const [previewImages, setPreviewImages] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [totalImages, setTotalImages] = useState(0);

  const urlParams = new URLSearchParams(window.location.search);
  const idFromUrl = urlParams.get("id");
  const id = idFromUrl || "";

  const [openModal, setOpenModal] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [fileSelected, setFileSelected] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAssetId(id);
  }, [id, setAssetId]);

  useEffect(() => {
    if (assetId.length >= 5) {
      setLoadingData(true);
      fetchLocalAndGlobalMatImgs();
    } else {
      setPreviewImages([]);
    }
  }, [assetId]);

  const fetchLocalAndGlobalMatImgs = async () => {
    try {
      setLoading(true);
      const response = await getLocalAndGlobalAssetImages(assetId);
      
      // Handle the nested response structure: [{ "images": [...] }]
      let imageData = [];
      if (response?.data && Array.isArray(response.data)) {
        // Check if it's an array of objects with images property
        if (response.data.length > 0 && response.data[0].images && Array.isArray(response.data[0].images)) {
          imageData = response.data[0].images;
        }
        // Or if it's directly an array of image URLs
        else if (typeof response.data[0] === 'string') {
          imageData = response.data;
        }
      } else if (response?.data?.images && Array.isArray(response.data.images)) {
        // Handle case where data is an object with images property
        imageData = response.data.images;
      }
      
      setPreviewImages(imageData);
      setTotalImages(imageData.length);
    } catch (error) {
      setPreviewImages([]);
      setTotalImages(0);
      Logger.printStackTrace(error);
    } finally {
      setLoadingData(false);
      setLoading(false);
    }
  };
  

  const handleFileChange = (event) => {
    setSelectedImages(event.target.files);
    const selectedFiles = event.target.files;
    const selectedFilesCount = selectedFiles.length;
    setFileSelected(selectedFilesCount);
  };

  const handleSubmit = async () => {
    if (!assetId) {
      handleToastOpen("error", AssetNumberRequired);
      return;
    }
    if (!selectedImages || selectedImages.length === 0) {
      handleToastOpen("error", ImageRequired);
      return;
    }
    if (selectedImages.length > 5) {
      handleToastOpen("error", MaxImageSelect);
      return;
    }
    if (selectedImages.length + totalImages > 5) {
      handleToastOpen("error", MaxImageUpload);
      return;
    }
    // Validate each image size and total size
    let totalSize = 0;

    for (const image of selectedImages) {
      totalSize += image.size;

      if (image.size > maxSizePerFile) {
        handleToastOpen("error", imageSize);
        return;
      }
    }
    setLoading(true);
    try {
      await ImageUpload(
        assetId,
        selectedImages,
        handleToastOpen
      );
      await fetchLocalAndGlobalMatImgs();
      // Clear selected images after successful upload
      setSelectedImages([]);
      setFileSelected(0); // Reset file selected count
      const fileInput = document.getElementById("image-id");
      if (fileInput) {
        fileInput.value = ""; // Reset file input value
      }
    } catch (error) {
      Logger.printStackTrace(error);
    } finally {
    setLoading(false); // Set loading to false after completion
  }
  };

  const handleDeleteClick = (imageUrl) => {
    setImageUrl(imageUrl);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleConfirmDelete = async () => {
    setLoading(true)
    try {
      const response = await deleteLocalAndGlobalAssetImages(assetId, imageUrl);
      if (response) {
        setImageUrl("");
        handleCloseModal();
        await fetchLocalAndGlobalMatImgs();
        handleToastOpen("success", AssetImageDeleted);
        
      }
    } catch (error) {
      Logger.printStackTrace(error);
      handleToastOpen("error", "An error occurred while deleting the image.");
    } finally {
      setLoading(false); // Set loading to false after completion
    }
  };
  
  function getImageName(imageUrl) {
    
    // Check if imageUrl is a string
    if (typeof imageUrl !== 'string') {
      console.warn('imageUrl is not a string:', imageUrl);
      return 'Unknown';
    }
    
    // Check if it's a URL or just a filename
    if (imageUrl.includes('/')) {
      const urlParts = imageUrl.split("/");
      return urlParts[urlParts.length - 1];
    }
    
    // If it's already just a filename, return as is
    return imageUrl;
  }

  return {
    imageUrl,
    assetId,
    setAssetId,
    selectedImages,
    handleFileChange,
    handleSubmit,
    previewImages,
    loadingData,
    handleConfirmDelete,
    handleCloseModal,
    handleDeleteClick,
    openModal,
    getImageName,
    fileSelected,
    loading,
  };
};

export default ImageUploadVM;